import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

interface ChecklistSection {
  title: string
  items: { task: string; detail: string }[]
}

const checklist: ChecklistSection[] = [
  {
    title: 'Before Making an Offer',
    items: [
      {
        task: 'Verify rent roll against bank statements or leases',
        detail:
          'Sellers sometimes inflate rents. Ask for 12 months of bank deposits or signed leases to confirm actual collected rent.',
      },
      {
        task: 'Pull comparable sales (last 6 months, 1 mile radius)',
        detail:
          'Confirm the asking price is in line with recent sales of similar properties. Use county records, not Zillow estimates.',
      },
      {
        task: 'Check county tax records',
        detail: 'Verify ownership, tax amount, lot size, and any liens or back taxes. Cuyahoga County has this online.',
      },
      {
        task: 'Run preliminary underwriting',
        detail:
          'Calculate cap rate, cash-on-cash, and DSCR using conservative assumptions before spending money on inspections.',
      },
      {
        task: 'Drive the neighborhood at different times',
        detail:
          'Visit during the day, evening, and weekend. Check street condition, parking, occupied vs. boarded-up properties nearby.',
      },
    ],
  },
  {
    title: 'Under Contract (Inspection Period)',
    items: [
      {
        task: 'Full property inspection',
        detail:
          'Hire a licensed inspector experienced with multifamily. Budget $300-$600 for a duplex. Non-negotiable.',
      },
      {
        task: 'Sewer scope',
        detail:
          '$150-$250 and worth every penny. Sewer line replacement can cost $5,000-$15,000. Especially important on older Cleveland properties with clay pipes.',
      },
      {
        task: 'Roof assessment',
        detail:
          'Flat roofs are common on Cleveland multifamily. Get age, condition, and estimated remaining life. Budget $8,000-$15,000 for replacement.',
      },
      {
        task: 'Verify utility setup',
        detail: 'Are utilities separately metered? If not, factor owner-paid utilities into your expense projections.',
      },
      {
        task: 'Review all leases',
        detail:
          'Check lease terms, security deposits held, any concessions, and lease expiration dates. Month-to-month gives flexibility; long-term gives stability.',
      },
      {
        task: 'Get insurance quotes',
        detail:
          'Call 2-3 agents for landlord policy quotes. Cleveland properties can vary significantly based on age, construction type, and claims history.',
      },
      {
        task: 'Verify lead paint status',
        detail:
          'Pre-1978 buildings require lead disclosure. Ohio has specific requirements for rental properties. Budget for lead risk assessments if needed.',
      },
      {
        task: 'Check for open permits or code violations',
        detail:
          'Call the city building department. Open violations can require expensive remediation and block insurance or financing.',
      },
    ],
  },
  {
    title: 'Financing & Closing',
    items: [
      {
        task: 'Get appraisal ordered through lender',
        detail:
          'The bank orders this, not you. Investment property appraisals use income approach (cap rate) and comparable sales.',
      },
      {
        task: 'Review title commitment',
        detail:
          'Look for liens, easements, encroachments, or title defects. Title insurance protects you, but read the exceptions.',
      },
      {
        task: 'Set up property management',
        detail: 'If using a PM company, have them signed before close so they can handle day-one tenant communication.',
      },
      {
        task: 'Set up business bank account',
        detail:
          'Separate all rental income and expenses from personal accounts. Required for clean accounting and tax preparation.',
      },
      {
        task: 'Arrange landlord insurance effective at close',
        detail: 'Your lender will require proof of insurance before funding. Have the policy bound and ready.',
      },
      {
        task: 'Final walkthrough',
        detail:
          "Verify property condition hasn't changed since inspection. Check that all agreed repairs were completed.",
      },
    ],
  },
  {
    title: 'Post-Close (First 30 Days)',
    items: [
      {
        task: 'Introduce yourself (or PM) to tenants',
        detail:
          'Send a professional letter with new payment instructions, maintenance request process, and contact information.',
      },
      {
        task: 'Change locks on vacant units',
        detail: "Standard security practice. Don't change occupied unit locks without proper notice.",
      },
      {
        task: 'Set up accounting system',
        detail:
          'Track every dollar from day one. Property-level P&L, not a combined spreadsheet. Software like Stessa is free and works well.',
      },
      {
        task: 'Create a CapEx reserve schedule',
        detail:
          'Based on inspection findings, estimate when major systems will need replacement and start reserving monthly.',
      },
      {
        task: 'File for any applicable tax abatements',
        detail:
          'Some Cleveland neighborhoods have tax incentives for property improvements. Check with the city before starting any rehab.',
      },
    ],
  },
]

export default function DueDiligenceChecklistPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 py-12">
      <Link to="/resources" className="text-amber-600 hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-3xl font-bold">Pre-Close Due Diligence Checklist</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            Due diligence is where you verify that a deal is actually what it appears to be. Skipping steps here is how
            investors end up with expensive surprises. This is the checklist we use at Fairmeadow Capital for every
            acquisition.
          </p>
        </CardContent>
      </Card>

      {checklist.map((section, sectionIndex) => (
        <div key={section.title}>
          {sectionIndex > 0 && <Separator />}
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{section.title}</h2>
            <div className="space-y-3">
              {section.items.map((item) => (
                <Card key={item.task}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <div className="border-muted-foreground/30 mt-0.5 h-5 w-5 shrink-0 rounded border" />
                      <div>
                        <p className="font-semibold">{item.task}</p>
                        <p className="text-muted-foreground mt-1 text-sm">{item.detail}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>
      ))}

      <Card>
        <CardContent className="p-6">
          <h2 className="mb-3 text-xl font-semibold">Estimated Due Diligence Costs</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {[
              { item: 'Property Inspection', cost: '$300-$600' },
              { item: 'Sewer Scope', cost: '$150-$250' },
              { item: 'Appraisal', cost: '$400-$700' },
              { item: 'Title Search', cost: '$200-$400' },
              { item: 'Lead Assessment', cost: '$200-$500' },
              { item: 'Survey (if needed)', cost: '$400-$800' },
            ].map((item) => (
              <div key={item.item} className="bg-muted/50 rounded-lg p-3">
                <div className="text-muted-foreground text-xs">{item.item}</div>
                <div className="font-semibold">{item.cost}</div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-3 text-sm">
            Budget $1,500-$3,000 total for due diligence on a typical Cleveland duplex or triplex. This is insurance
            against a $100,000+ mistake.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
